import {escapeHTML} from "./lib/escapeHTML";
import {jQuery, moment} from "./boot";
import {AppConfig} from "./AppConfig";
import {CounterListItem} from "./dto/com.rico.sb2.service";
import {Messages} from "./messages/Messages";
import {bootstrapPrompt} from "./lib/bootstrapModal";
import {apiPost} from "./lib/SecuredAjax";
import {isAjaxResponseOk, toastFetchError} from "./lib/fetch";
import {showToastInfo} from "./lib/boostrapToast";
import {invFrac3, invFracMax3} from "./lib/formatTool";

export function formatDate() {
	const format = new Intl.DateTimeFormat(document.documentElement.lang, {day: "numeric", month: "short"});
	return (i: any) => i != null ? format.format(i) : undefined;
}


const messages = new Messages();
const h = escapeHTML;

export class CounterListPage {
	private readonly canEdit: boolean;
	private readonly canReset: boolean;
	private readonly table: HTMLTableElement;

	constructor({canEdit = false, canReset = false}: { canEdit: boolean, canReset: boolean }) {
		this.canEdit = canEdit;
		this.canReset = canReset;

		this.table = document.getElementById('table') as HTMLTableElement;

		const bootstrapTableOptions = {
			url: `${AppConfig.CP}/counters/listPage`,
			sidePagination: 'server',
			rowStyle: () => ({classes: 'align-center'}),
			responseHandler: (res: any) => ({total: res.totalElements, rows: res.content}),
			columns: [
				{
					class: 'w-0',
				},
				{},
				{},
				{
					formatter: (value: any, row: CounterListItem, index: any) => {
						if ((row.flags & 1) === 1)
							return `<span class="badge text-bg-success">${messages.get('switch.on')}</span>`;
						return `<span class="badge text-bg-secondary">${messages.get('switch.off')}</span>`;
					}
				},
				{class: 'text-end', formatter: (value: number) => invFracMax3(value)},
				{
					formatter: (value: any) => {
						if (value) {
							return moment(value).format('DD.MM.YYYY HH:mm:ss')
						}
						return '';
					}
				},
				{class: 'text-end', formatter: (value: number) => invFracMax3(value)},
				{
					formatter: (value: any) => {
						if (value) {
							return moment(value).format('DD.MM.YYYY HH:mm:ss')
						}
						return '';
					}
				},
				{
					formatter: (value: number) => invFrac3(value)
				},
				{
					formatter: (value: number) => invFrac3(value)
				}
			],

			onCheck: () => this.updateButtonState(),
			onCheckAll: () => this.updateButtonState(),
			onUncheck: () => this.updateButtonState(),
			onUncheckAll: () => this.updateButtonState(),
			onResetView: () => this.updateButtonState(),
			onPostBody: () => {
				Array.from(this.table.querySelectorAll('input[type="checkbox"]'))
					.forEach(checkbox => {
						checkbox.classList.add('form-check-input')
						const block = checkbox.parentElement?.parentElement;
						if (block && block.classList.contains('th-inner')) block.classList.add('form-check', 'mb-0');
						if (block && block.classList.contains('bs-checkbox')) block.classList.add('pb-0');
					})
			}
		};

		if (canEdit) {
			bootstrapTableOptions.columns.push(
				{
					formatter: (value: any, row: CounterListItem) => {
						return `<a href="${AppConfig.CP}/counters/${row.id}/edit" class="btn btn-sm btn-secondary">${messages.get('button.edit')}</a>`;
					}
				}
			)
		}

		const tableJq = jQuery(this.table);
		tableJq.bootstrapTable(bootstrapTableOptions)

		document.querySelectorAll<HTMLButtonElement>('[data-bind="resetSelected"]').forEach(node => {
			node.addEventListener('click', () => this.resetSelected());
		})
		document.querySelectorAll<HTMLButtonElement>('[data-bind="deleteSelected"]').forEach(node => {
			node.addEventListener('click', () => this.deleteSelected());
		})

		this.updateButtonState();
	}

	private getSelection(): number[] {
		return jQuery(this.table).bootstrapTable('getSelections').map((n: any) => n.id)
	}

	private updateButtonState() {
		const selectionEmpty = this.getSelection().length == 0;
		document.querySelectorAll<HTMLButtonElement>('[data-bind="resetSelected"]').forEach(node => node.disabled = selectionEmpty);
		document.querySelectorAll<HTMLButtonElement>('[data-bind="deleteSelected"]').forEach(node => node.disabled = selectionEmpty);
	}

	private async resetSelected() {
		const selected = this.getSelection().filter(id => id > 0);
		CounterListPage.resetSelected(selected, 'Сбросить выбранные счетчики?')
			.then(success => {
				if (success) jQuery(this.table).bootstrapTable('refresh')
			})
			.catch(error => toastFetchError(error))
	}

	static async resetSelected(ids: number[], confirmText: string) {
		if (ids.length == 0) return false;

		const confirm = await bootstrapPrompt({
			title: confirmText,
			body: `Количество счетчиков для сброса: ${ids.length}`,
			buttonOk: messages.get('boolean.yes'),
			buttonOkClass: 'btn-danger'
		});
		if (!confirm) return false;

		return apiPost(`${AppConfig.CP}/counters/resetCounters`, ids)
			.then(res => isAjaxResponseOk(res) ? res.value : Promise.reject(res.message))
			.then(success => {
				if (!success) return Promise.reject(500)
				showToastInfo('Счетчики сброшены')
				return true;
			})
	}

	public async deleteSelected() {
		const selected = this.getSelection().filter(id => id > 0);
		return CounterListPage.deleteSelected(selected, 'Удалить выбранные счетчики?')
			.then(success => {
				if (success) jQuery(this.table).bootstrapTable('refresh')
			})
			.catch(error => toastFetchError(error));
	}

	static async deleteSelected(ids: number[], confirmText: string) {
		if (ids.length == 0) return false;

		const confirm = await bootstrapPrompt({
			title: confirmText,
			body: `Количество счетчиков для удаления: ${ids.length}`,
			buttonOk: messages.get('boolean.yes'),
			buttonOkClass: 'btn-danger'
		});
		if (!confirm) return false;

		return apiPost(`${AppConfig.CP}/counters/deleteCounters`, ids)
			.then(res => isAjaxResponseOk(res) ? res.value : Promise.reject(res.message))
			.then(success => {
				if (!success) return Promise.reject(500)
				showToastInfo('Счетчики сброшены');
				return true;
			})
	}
}

export class CounterEditPage {
	private readonly id: number;

	constructor(id: number) {
		this.id = id;
		document.querySelectorAll<HTMLButtonElement>('[data-bind="resetCounter"]').forEach(node => {
			node.addEventListener('click', () => this.resetCounter());
		})
		document.querySelectorAll<HTMLButtonElement>('[data-bind="deleteCounter"]').forEach(node => {
			node.addEventListener('click', () => this.deleteCounter());
		})
	}

	private resetCounter() {
		CounterListPage.resetSelected([this.id], 'Сбросить счетчик?')
			.then(success => {
				if (success) {
					document.location = `${AppConfig.CP}/counters/${this.id}/edit`
				}
			})
			.catch(error => toastFetchError(error))
	}

	private deleteCounter() {
		CounterListPage.deleteSelected([this.id], 'Удалить счетчик?')
			.then(success => {
				if (success) {
					document.location = `${AppConfig.CP}/counters`;
				}
			})
			.catch(error => toastFetchError(error))
	}
}